import { useState, useEffect } from "react";
import { useCustomerAuth } from "@/context/CustomerAuth";
import { formatApiError } from "@/lib/api";
import { X, Loader2, Snowflake, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full bg-[#050810] border border-[#1A2642] text-white rounded-md px-4 py-3 text-sm placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition";
const labelCls = "block text-[11px] font-bold uppercase tracking-[0.18em] text-[#94A3B8] mb-2";

export default function AuthDialog({ open, onClose, onSuccess }) {
  const { login, register, forgotPassword } = useCustomerAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "" });

  useEffect(() => {
    if (open) { setMode("login"); setError(""); setInfo(""); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setInfo("");
    try {
      if (mode === "forgot") {
        const r = await forgotPassword(form.email);
        setInfo(r.message || "Si un compte existe, un email de réinitialisation a été envoyé.");
        toast.success("Email de réinitialisation envoyé (si le compte existe).");
        setLoading(false);
        return;
      }
      let cust;
      if (mode === "login") cust = await login(form.email, form.password);
      else cust = await register({ first_name: form.first_name, last_name: form.last_name, email: form.email, password: form.password, phone: form.phone });
      toast.success(mode === "login" ? `Bon retour, ${cust.first_name} !` : `Bienvenue, ${cust.first_name} !`);
      onSuccess?.(cust);
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="auth-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md glass border border-[#1A2642] rounded-2xl p-6 sm:p-8 animate-float-up">
        <button onClick={onClose} data-testid="auth-close" className="absolute top-4 right-4 text-[#94A3B8] hover:text-white transition"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-2 mb-1">
          <Snowflake className="w-6 h-6 text-[#00B4D8]" />
          <span className="font-heading font-extrabold text-lg">Mon compte ClimOmax13</span>
        </div>
        <p className="text-sm text-[#94A3B8] mb-5">{mode === "login" ? "Connectez-vous pour gérer vos rendez-vous." : mode === "register" ? "Créez votre compte en quelques secondes." : "Saisissez votre email pour recevoir un lien de réinitialisation."}</p>

        {mode !== "forgot" && (
          <div className="flex gap-1 p-1 bg-[#050810] border border-[#1A2642] rounded-lg mb-6">
            <button onClick={() => { setMode("login"); setError(""); setInfo(""); }} data-testid="tab-login"
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${mode === "login" ? "bg-[#00B4D8] text-[#050810]" : "text-[#94A3B8] hover:text-white"}`}>Connexion</button>
            <button onClick={() => { setMode("register"); setError(""); setInfo(""); }} data-testid="tab-register"
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${mode === "register" ? "bg-[#00B4D8] text-[#050810]" : "text-[#94A3B8] hover:text-white"}`}>Créer un compte</button>
          </div>
        )}

        {error && <div className="bg-[#EF476F]/15 border border-[#EF476F]/30 text-[#EF476F] text-sm rounded-md px-3 py-2 mb-4" data-testid="auth-error">{error}</div>}
        {info && <div className="bg-[#06D6A0]/15 border border-[#06D6A0]/30 text-[#06D6A0] text-sm rounded-md px-3 py-2 mb-4" data-testid="auth-info">{info}</div>}

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Prénom *</label>
                <input required className={inputCls} data-testid="auth-firstname" placeholder="Lucas" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><label className={labelCls}>Nom</label>
                <input className={inputCls} data-testid="auth-lastname" placeholder="Martin" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
          )}
          <div><label className={labelCls}>Email *</label>
            <input required type="email" className={inputCls} data-testid="auth-email" placeholder="vous@email.fr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          {mode === "register" && (
            <div><label className={labelCls}>Téléphone</label>
              <input className={inputCls} data-testid="auth-phone" placeholder="06 12 34 56 78" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          )}
          {mode !== "forgot" && (
            <div><label className={labelCls}>Mot de passe *</label>
              <input required type="password" minLength={6} className={inputCls} data-testid="auth-password" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          )}
          {mode === "login" && (
            <button type="button" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} data-testid="forgot-password-link"
              className="text-xs font-semibold text-[#00B4D8] hover:underline">Mot de passe oublié ?</button>
          )}
          <button type="submit" disabled={loading} data-testid="auth-submit"
            className="w-full bg-[#FFD60A] text-[#050810] font-bold rounded-full py-3.5 hover:bg-[#FFEA00] disabled:opacity-50 transition flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Envoyer le lien"}
          </button>
          {mode === "forgot" && (
            <button type="button" onClick={() => { setMode("login"); setError(""); setInfo(""); }} data-testid="back-to-login"
              className="w-full text-center text-xs font-semibold text-[#94A3B8] hover:text-white transition">← Retour à la connexion</button>
          )}
        </form>
      </div>
    </div>
  );
}
